import type { ChangedOccurrence } from '../types/changed-occurrence'
import type { SpellIssue } from '../types/spell-issue'

/**
 * The instances that were open in `previous` and are `changed` in `next`: what a Change or Change
 * All has just asked to be written into the text. Matched by issue id and position, which a
 * transition never moves. A transition swaps the whole instance for its resolution, so where the
 * instance is comes from the open one it replaced.
 */
export function newlyChangedOccurrences(
  previous: readonly SpellIssue[],
  next: readonly SpellIssue[]
): ChangedOccurrence[] {
  const previousById = new Map(previous.map((issue) => [issue.id, issue]))

  return next.flatMap((issue) => {
    const before = previousById.get(issue.id)

    if (!before) return []

    return issue.occurrences.flatMap((occurrence, index) => {
      const wasOpen = before.occurrences[index]

      return occurrence.status === 'changed' && wasOpen?.status === 'open'
        ? [{ issueId: issue.id, index, occurrence: { ...wasOpen, ...occurrence } }]
        : []
    })
  })
}

/**
 * Forgets every `changed` choice, for once a scan of the rewritten text has landed: those instances
 * are gone from the text, so a choice kept for them would be matched by position onto the next
 * instance of the word, such as the same typo typed again, and hide it. The other choices stay, and
 * an issue left with none is dropped.
 */
export function forgetWrittenChanges(issues: readonly SpellIssue[]): SpellIssue[] {
  return issues.flatMap((issue) => {
    const occurrences = issue.occurrences.filter((o) => o.status !== 'changed')

    if (occurrences.length === issue.occurrences.length) return [issue]

    return occurrences.some((o) => o.status !== 'open') ? [{ ...issue, occurrences }] : []
  })
}

/** Puts `occurrences` back to `open`, for a change that could not be written into the text. */
export function reopenOccurrences(
  issues: readonly SpellIssue[],
  occurrences: readonly ChangedOccurrence[]
): SpellIssue[] {
  if (occurrences.length === 0) return [...issues]

  const reopened = new Set(occurrences.map(({ issueId, index }) => `${issueId}\u0000${index}`))

  return issues.map((issue) =>
    issue.occurrences.some((_, index) => reopened.has(`${issue.id}\u0000${index}`))
      ? {
          ...issue,
          occurrences: issue.occurrences.map((occurrence, index) => {
            if (!reopened.has(`${issue.id}\u0000${index}`)) return occurrence

            const { replacement: _replacement, ...rest } = occurrence

            return { ...rest, status: 'open' as const }
          }),
        }
      : issue
  )
}
